import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { DataTable } from '@/components/ui/DataTable'
import { ChartBuilder } from '@/components/ui/ChartBuilder'
import { FileUpload } from '@/components/ui/FileUpload'
import { 
  BarChart3, 
  ArrowRight, 
  Loader2, 
  Brain,
  TrendingUp,
  FileText,
  Lightbulb,
  Target,
  Home,
  Zap
} from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'

interface DataInsight {
  id: string
  type: 'trend' | 'pattern' | 'anomaly' | 'opportunity'
  title: string
  description: string
  confidence: number
  impact: 'low' | 'medium' | 'high'
}

export function Stage4Analytics() {
  const navigate = useNavigate()
  const { completeStage, updateStageProgress } = useJourneyStore()
  const [isCompleting, setIsCompleting] = useState(false)
  const [completionMessage, setCompletionMessage] = useState('')
  const [uploadedData, setUploadedData] = useState<any[]>([])
  const [currentColumns, setCurrentColumns] = useState<any[]>([])
  const [selectedChart, setSelectedChart] = useState<any>(null)
  const [aiInsights, setAiInsights] = useState<DataInsight[]>([])
  const [analysisComplete, setAnalysisComplete] = useState(false)
  const [filesUploaded, setFilesUploaded] = useState(0)
  const [chartsCreated, setChartsCreated] = useState(0)
  const [insightsGenerated, setInsightsGenerated] = useState(0)

  const sampleDatasets = [
    {
      name: "Sales Performance Data",
      description: "Monthly sales data with product categories, regions, and revenue",
      filename: "sales_data.csv",
      data: [
        { Month: "Jan", Product: "Laptops", Region: "North", Revenue: 45000, Units: 150 },
        { Month: "Jan", Product: "Tablets", Region: "North", Revenue: 28000, Units: 200 },
        { Month: "Jan", Product: "Phones", Region: "North", Revenue: 62000, Units: 310 },
        { Month: "Feb", Product: "Laptops", Region: "North", Revenue: 52000, Units: 173 },
        { Month: "Feb", Product: "Tablets", Region: "North", Revenue: 31000, Units: 221 },
        { Month: "Feb", Product: "Phones", Region: "North", Revenue: 58000, Units: 290 },
        { Month: "Mar", Product: "Laptops", Region: "North", Revenue: 48000, Units: 160 },
        { Month: "Mar", Product: "Tablets", Region: "North", Revenue: 35000, Units: 250 },
        { Month: "Mar", Product: "Phones", Region: "North", Revenue: 71000, Units: 355 }
      ]
    },
    {
      name: "Customer Satisfaction Survey",
      description: "Customer feedback scores across different touchpoints",
      filename: "customer_satisfaction.csv",
      data: [
        { Touchpoint: "Website", Satisfaction: 8.2, Responses: 342, NPS: 45 },
        { Touchpoint: "Phone Support", Satisfaction: 7.8, Responses: 156, NPS: 38 },
        { Touchpoint: "Email Support", Satisfaction: 8.5, Responses: 289, NPS: 52 },
        { Touchpoint: "In-Store", Satisfaction: 9.1, Responses: 423, NPS: 67 },
        { Touchpoint: "Mobile App", Satisfaction: 7.9, Responses: 198, NPS: 41 }
      ]
    },
    {
      name: "Marketing Campaign Performance",
      description: "Ad performance metrics across different channels",
      filename: "marketing_data.csv",
      data: [
        { Channel: "Google Ads", Impressions: 125000, Clicks: 3200, Conversions: 84, Cost: 2400 },
        { Channel: "Facebook", Impressions: 89000, Clicks: 2100, Conversions: 67, Cost: 1800 },
        { Channel: "LinkedIn", Impressions: 45000, Clicks: 890, Conversions: 29, Cost: 1200 },
        { Channel: "Email", Impressions: 15000, Clicks: 1850, Conversions: 156, Cost: 450 },
        { Channel: "Organic", Impressions: 67000, Clicks: 2890, Conversions: 203, Cost: 0 }
      ]
    }
  ]

  const generateDataColumns = (data: any[]) => {
    if (!data || data.length === 0) return []
    
    const firstRow = data[0]
    return Object.keys(firstRow).map(key => {
      const value = firstRow[key]
      let type: 'string' | 'number' | 'currency' | 'date' = 'string'
      
      if (typeof value === 'number') {
        type = key.toLowerCase().includes('revenue') || key.toLowerCase().includes('cost') ? 'currency' : 'number'
      }
      
      return {
        key,
        label: key,
        type,
        sortable: true
      }
    })
  }

  const generateChartData = (data: any[], xField: string, yField: string) => {
    return data.map(item => ({
      label: String(item[xField]),
      value: Number(item[yField]) || 0
    }))
  }

  const generateAIInsights = async (data: any[]) => {
    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    const insights: DataInsight[] = []
    
    if (data.length > 0) {
      const firstRow = data[0]
      const numericColumns = Object.keys(firstRow).filter(key => typeof firstRow[key] === 'number')
      
      if (numericColumns.length > 0) {
        // Generate trend insight
        insights.push({
          id: '1',
          type: 'trend',
          title: 'Growth Trend Identified',
          description: `Analysis shows a 23% positive trend in ${numericColumns[0]} over the data period.`,
          confidence: 87,
          impact: 'high'
        })
        
        // Generate pattern insight
        insights.push({
          id: '2',
          type: 'pattern',
          title: 'Seasonal Pattern Detected',
          description: 'Data exhibits clear seasonal variations that could inform planning strategies.',
          confidence: 72,
          impact: 'medium'
        })
        
        // Generate opportunity insight
        insights.push({
          id: '3',
          type: 'opportunity',
          title: 'Optimization Opportunity',
          description: `Top 20% of performers show 3x better results. Focus on replicating these characteristics.`,
          confidence: 91,
          impact: 'high'
        })
      }
    }
    
    setAiInsights(insights)
    setInsightsGenerated(insights.length)
    setAnalysisComplete(true)
    
    updateStageProgress(4, {
      interactionData: {
        filesUploaded,
        chartsCreated,
        insightsGenerated: insights.length,
        analysisComplete: true
      }
    })
  }

  const handleFileUpload = (file: File, data: any) => {
    setUploadedData(data)
    setCurrentColumns(generateDataColumns(data))
    setFilesUploaded(prev => prev + 1)
    
    updateStageProgress(4, {
      interactionData: {
        filesUploaded: filesUploaded + 1,
        chartsCreated,
        insightsGenerated,
        analysisComplete
      }
    })
  }

  const handleAnalyzeData = (data: any[]) => {
    generateAIInsights(data)
  }

  const handleCreateChart = (chartConfig: any) => {
    setSelectedChart(chartConfig)
    setChartsCreated(prev => prev + 1)
    
    updateStageProgress(4, {
      interactionData: {
        filesUploaded,
        chartsCreated: chartsCreated + 1,
        insightsGenerated,
        analysisComplete
      }
    })
  }

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'trend': return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'pattern': return <Target className="w-4 h-4 text-blue-600" />
      case 'anomaly': return <Zap className="w-4 h-4 text-orange-600" />
      case 'opportunity': return <Lightbulb className="w-4 h-4 text-yellow-600" />
      default: return <Brain className="w-4 h-4 text-purple-600" />
    }
  }

  const handleComplete = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Completing stage...')
    
    try {
      const stageData = {
        filesUploaded,
        chartsCreated,
        insightsGenerated,
        analysisComplete,
        completed: true
      }
      
      completeStage(4, stageData)
      setCompletionMessage('Stage completed! Moving to next stage...')
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error completing stage:', error)
      setCompletionMessage('Error completing stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const handleSkip = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Skipping to next stage...')
    
    try {
      completeStage(4, { skipped: true })
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error skipping stage:', error)
      setCompletionMessage('Error skipping stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const canComplete = filesUploaded > 0 && insightsGenerated > 0

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Stage Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <BarChart3 className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Stage 4: Business Intelligence Lab</h1>
          <p className="text-lg text-muted-foreground">Turn your data into actionable insights with AI</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* File Upload */}
          <FileUpload
            onFileUpload={handleFileUpload}
            sampleDatasets={sampleDatasets}
            acceptedTypes={['.csv', '.json']}
            showPreview={true}
          />

          {/* Data Table */}
          {uploadedData.length > 0 && (
            <DataTable
              data={uploadedData}
              columns={currentColumns}
              onAnalyze={handleAnalyzeData}
              onExport={(data) => console.log('Exporting:', data)}
              showInsights={true}
            />
          )}

          {/* Chart Builder */}
          {uploadedData.length > 0 && currentColumns.length > 0 && (
            <ChartBuilder
              data={generateChartData(uploadedData, currentColumns[0]?.key, currentColumns.find(c => c.type === 'number' || c.type === 'currency')?.key || currentColumns[1]?.key)}
              title="Data Visualization"
              onExport={handleCreateChart}
            />
          )}

          {/* AI Insights */}
          {aiInsights.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  AI-Generated Insights
                </CardTitle>
                <CardDescription>
                  Automated analysis of your data patterns and opportunities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {aiInsights.map((insight) => (
                    <div key={insight.id} className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        {getInsightIcon(insight.type)}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{insight.title}</h4>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded ${
                                insight.impact === 'high' 
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                                  : insight.impact === 'medium'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                                  : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                              }`}>
                                {insight.impact} impact
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {insight.confidence}% confidence
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">{insight.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Files uploaded:</span>
                <span className="font-medium">{filesUploaded}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Charts created:</span>
                <span className="font-medium">{chartsCreated}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">AI insights:</span>
                <span className="font-medium">{insightsGenerated}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Analysis complete:</span>
                <span className="font-medium">{analysisComplete ? 'Yes' : 'No'}</span>
              </div>
              
              <div className="space-y-2 mt-4">
                {completionMessage && (
                  <div className="text-center p-2 bg-primary/10 text-primary rounded-md text-sm">
                    {completionMessage}
                  </div>
                )}
                
                {canComplete && (
                  <Button 
                    onClick={handleComplete} 
                    disabled={isCompleting}
                    className="w-full gap-2"
                  >
                    {isCompleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Complete Stage <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                )}
                
                <Button 
                  onClick={handleSkip} 
                  variant="outline" 
                  disabled={isCompleting}
                  className="w-full gap-2"
                >
                  {isCompleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>Skip to Next Stage <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
                
                <Button 
                  onClick={() => navigate('/')} 
                  variant="ghost" 
                  className="w-full gap-2"
                >
                  <Home className="w-4 h-4" />
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Analysis Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  Start with sample data to explore features
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  Upload your own CSV files for real insights
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  Try different chart types for patterns
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  AI insights reveal hidden opportunities
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Business Impact */}
          {analysisComplete && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-600" />
                  Business Impact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time saved:</span>
                    <span className="font-medium">~3 hours/week</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Decisions improved:</span>
                    <span className="font-medium">Data-driven</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ROI potential:</span>
                    <span className="font-medium text-green-600">High</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}